/**
 * Accessible, style-agnostic tab controller.
 *
 * Usage example:
 * <tabs-component>
 *   <div role="tablist">
 *     <button role="tab" aria-controls="p1" data-active>Tab 1</button>
 *     <button role="tab" aria-controls="p2">Tab 2</button>
 *   </div>
 *   <section id="p1" role="tabpanel">…</section>
 *   <section id="p2" role="tabpanel" hidden>…</section>
 * </tabs-component>
 *
 * Drop once into Shopify (or any site) and add <tabs-component> blocks as needed.
 *
 * Design notes
 * – Light DOM → global CSS can style internals.
 * – Self-contained state per instance.
 * – Progressive enhancement → all panels visible without JS.
 *
 * @extends HTMLElement
 */
class TabsComponent extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    // collect elements
    this.tabs = Array.from(this.querySelectorAll('[role="tab"]')).filter(
      (el) => el instanceof HTMLButtonElement
    );
    this.panels = Array.from(this.querySelectorAll('[role="tabpanel"]'));

    if (this.tabs.length !== this.panels.length) return;

    // pick initial tab
    this.current = this.tabs.findIndex((t) => t.hasAttribute("data-active"));
    if (this.current < 0) this.current = 0;
    this.#activate(this.current, false);

    // click handling
    this.tabs.forEach((tab, i) =>
      tab.addEventListener("click", () => this.#activate(i))
    );

    // keyboard handling
    this.addEventListener("keydown", (e) => {
      if (!["ArrowRight", "ArrowLeft", "Home", "End"].includes(e.key)) return;
      e.preventDefault();

      let idx = this.current;
      if (e.key === "ArrowRight") idx = (this.current + 1) % this.tabs.length;
      if (e.key === "ArrowLeft")
        idx = (this.current - 1 + this.tabs.length) % this.tabs.length;
      if (e.key === "Home") idx = 0;
      if (e.key === "End") idx = this.tabs.length - 1;

      this.#activate(idx);
      this.tabs[idx].focus();
    });
  }

  #activate(index) {
    this.tabs.forEach((tab, i) => {
      const active = i === index;
      tab.setAttribute("aria-selected", active ? "true" : "false");
      tab.tabIndex = active ? 0 : -1;
      this.panels[i].hidden = !active;
    });

    this.current = index;

    // if (this.panels[index]) {
    //   this.#refreshSlideshows(this.panels[index]);
    // }

    // Handle Slick sliders - refresh when switching to any tab
    if (this.panels[index]) {
      this.#refreshSlickSliders(this.panels[index]);
    }
  }

  #refreshSlideshows(panel) {
    // Handle slideshow-component (existing logic - unchanged)
    const slideshows = panel.querySelectorAll("slideshow-component");
    if (slideshows.length) {
      setTimeout(() => {
        slideshows.forEach((slideshow) => {
          const slides = slideshow.querySelectorAll("slideshow-slide");
          if (!slides.length) return;

          slides.forEach((slide) => {
            slide.setAttribute("aria-hidden", "false");
          });
        });
      }, 100);
    }
  }

  #refreshSlickSliders(panel) {
    const slickSliders = panel.querySelectorAll(
      `[class*="collection-tab__slider--${panel.getAttribute('data-section-id')}"]`
    );

    if (
      slickSliders.length &&
      typeof window.$ !== "undefined" &&
      window.$.fn.slick
    ) {
      // Just refresh existing Slick instances instead of reinitializing
      slickSliders.forEach((slider) => {
        if (window.$(slider).hasClass("slick-initialized")) {
          window.$(slider).slick("refresh");
        }
      });
    }
  }

  /* public API --------------------------------------------------------- */
  goTo(index) {
    this.#activate(index);
  }
  get currentIndex() {
    return this.current;
  }

  /* private fields ----------------------------------------------------- */
  tabs = [];
  panels = [];
  current = 0;
}

// Register the component with protection against duplicate registration
if (!customElements.get("tabs-component")) {
  customElements.define("tabs-component", TabsComponent);
}
